import { getConfig } from '@edx/frontend-platform';

import type { CourseOutline } from '../types';

interface CourseOutlineContent {
  id: string;
  unitName: string;
  blockData: string | undefined;
  parentId: string;
}

/**
 * Resolves the type of a course block to a human-readable string.
 *
 * Special handling for "Raw HTML" and its duplicates, which return `undefined`.
 * Otherwise, maps known block types (`problem`, `video`, `html`) to their display name,
 * or falls back to the block's type or `"Unknown"`.
 *
 * @param {CourseOutline} unitData - The unit-level course block.
 * @returns {string | undefined} Type of the block or `undefined` if filtered out.
 *
 * @example
 * courseOutlineProblemType({ type: "video", display_name: "Lecture 1" });
 * // "Lecture 1"
 */
function courseOutlineProblemType(unitData: CourseOutline): string | undefined {
  const typeMap: { [key: string]: string } = {
    problem: unitData.display_name,
    video: unitData.display_name,
    html: unitData.display_name,
  };

  if (unitData.display_name === 'Raw HTML' || unitData.display_name === "Duplicate of 'Raw HTML'") {
    return undefined;
  }

  return typeMap[unitData.type] || unitData.type || 'Unknown';
}

/**
 * Extracts a flat list of course outline contents from nested course blocks.
 *
 * Traverses through the hierarchy: data → semesters → subsections → units,
 * and collects unit details (name, id, and block type).
 *
 * @param {Object} data - The course data object.
 * @param {CourseOutline[]} [data.children] - List of semester-level course blocks.
 * @returns {CourseOutlineContent[]} Flattened list of course outline contents.
 *
 * @example
 * const outline = extractContents({ children: semesters });
 * // [
 * //   { unitName: "Intro", id: "block-v1:...", blockData: "video" },
 * //   { unitName: "Quiz 1", id: "block-v1:...", blockData: "problem" }
 * // ]
 */
export function extractContents(data: { children?: CourseOutline[] }): CourseOutlineContent[] {
  return (data.children || []).flatMap((semesterData: CourseOutline) => (
    semesterData.children || []).flatMap((subsectionData: CourseOutline) => ({
    unitName: subsectionData.display_name,
    id: subsectionData.id,
    blockData: courseOutlineProblemType(subsectionData),
    parentId: semesterData.id,
  })));
}

/**
 * Returns the ID of the first unit in the course outline.
 *
 * @param {CourseOutlineContent[]} units - List of course units.
 * @returns {string} ID of the first unit, or an empty string if none exist.
 *
 * @example
 * getFirstUnitId([{ id: "unit-id", unitName: "Intro", blockData: "video" }]);
 * // "unit-id"
 */
export const getFirstUnitId = (units: CourseOutlineContent[]): string => (units.length > 0 ? units[0].id : '');

/**
 * Returns the ID of the first section in the course outline.
 *
 * @param {CourseOutline[]} outline - The course outline data, which may contain nested children.
 * @returns {string} ID of the first section, or an empty string if the outline is empty.
 *
 * @example
 * // Returns 'section1'
 * const outline = [{
 *   id: 'section1',
 *   children: [
 *     { id: 'seq-id-1', children: [] },
 *     { id: 'seq-id-2', children: [] }
 *   ]
 * }];
 * getFirstSequenceId(outline);
 */
export const getFirstSequenceId = (outline: CourseOutline[]): string => outline?.[0]?.id || '';

/**
 * Returns an object containing the ID and parent ID of the unit following the given `currentId`.
 *
 * If the current unit is not found or is the last in the list,
 * returns an object with the current unit's ID and its parent ID.
 *
 * @param {CourseOutlineContent[]} units - List of course units.
 * @param {string} currentId - The ID of the current unit.
 * @returns {{unitId: string, parentId: string}} Object containing the next unit's ID and parent ID,
 * or the current unit's ID and parent ID if at the end or not found.
 *
 * @example
 * const units = [
 *   { id: "unit-id-1", parentId: "section1", unitName: "Intro", blockData: "video" },
 *   { id: "unit-id-2", parentId: "section1", unitName: "Quiz", blockData: "problem" }
 * ];
 * getNextUnitInfo(units, "unit-id-1");
 * // { unitId: "unit-id-2", parentId: "section1" }
 *
 * getNextUnitInfo(units, "unit-id-2");
 * // { unitId: "unit-id-2", parentId: "section1" } (last unit)
 */
interface NextUnitInfo {
  unitId: string;
  parentId: string;
}

export const getNextUnitInfo = (units: CourseOutlineContent[], currentId: string): NextUnitInfo => {
  const index = units.findIndex(unit => unit.id === currentId);

  if (index !== -1 && index < units.length - 1) {
    return {
      unitId: units[index + 1].id,
      parentId: units[index + 1].parentId,
    };
  }

  return { unitId: currentId, parentId: units[index]?.parentId };
};

/**
 * Returns the first available unit from the course outline.
 *
 * Looks at the first child (`data.children[0]`) and iterates through its
 * first `maxAttempts` children to find a valid unit.
 * If a unit exists (and is not empty), returns it immediately.
 * If none are found, returns `null`.
 *
 * @param data - Root course outline object.
 * @param maxAttempts - Maximum number of children to check. Defaults to 2.
 *
 * @example
 * const firstUnit = getFirstAvailableUnit(courseData);
 * if (firstUnit) {
 *   console.log(firstUnit.id, firstUnit.display_name);
 * }
 */
export function getFirstAvailableUnit(data: CourseOutline | null, maxAttempts = 2): string {
  if (!data || !data.children || data.children.length === 0) {
    return '';
  }

  // Iterate through the first two elements under children[0].children
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const element = data.children[0]?.children?.[attempt];

    if (element) {
      // If a non-"Raw HTML" element is found, return it
      return element?.id;
    }
  }

  return '';
}

/**
 * Returns the base LMS learning MFE URL.
 *
 * Ensures the URL contains `/learning`.
 * If `LEARNING_BASE_URL` already has `/learning`, it is used as-is.
 *
 * @returns {string} Base learning MFE URL.
 *
 * @example
 * // With LEARNING_BASE_URL = "https://example.com"
 * getLmsLearningUrl();
 * // "https://example.com/learning"
 *
 * @example
 * // With LEARNING_BASE_URL = "https://example.com/learning"
 * getLmsLearningUrl();
 * // "https://example.com/learning"
 */
function getLmsLearningUrl() {
  let learningMfeUrl = getConfig().LEARNING_BASE_URL;

  if (!getConfig().LEARNING_BASE_URL.includes('learning')) {
    learningMfeUrl = `${getConfig().LEARNING_BASE_URL}/learning`;
  }

  return learningMfeUrl;
}

/**
 * Builds a unit URL for a given course and block, then passes it to a callback.
 *
 * @param {string} courseID - The course identifier.
 * @param {string} subsectionId - The parent identifier (sequence ID).
 * @param {string} blockID - The block identifier (unit ID).
 * @param {(url: string) => void} callback - Function that receives the constructed URL.
 *
 * @example
 * getLmsCourseUnitUrl("course-v1:Demo+T101+2025", "block-v1:Demo+T101+2025+type@video", (url) => {
 *   console.log(url);
 *   // "https://example.com/learning/course/course-v1:Demo+T101+2025/block-v1:Demo+T101+2025+type@video"
 * });
 */
export function getLmsCourseUnitUrl(
  courseID: string,
  subsectionId: string,
  blockID: string,
  callback: (url: string) => void,
): void {
  callback(`${getLmsLearningUrl()}/course/${courseID}/${subsectionId}/${blockID}`);
}

/**
 * Builds a notes URL for a given course, then passes it to a callback.
 *
 * @param {string} courseID - The course identifier.
 * @param {(url: string) => void} callback - Function that receives the constructed URL.
 *
 * @example
 * getLmsCourseNotesUrl("course-v1:Demo+T101+2025", (url) => {
 *   console.log(url);
 *   // "https://example.com/courses/course-v1:Demo+T101+2025/edxnotes"
 * });
 */
export function getLmsCourseNotesUrl(courseID: string, callback: (url: string) => void): void {
  callback(`${getConfig().LMS_BASE_URL}/courses/${courseID}/edxnotes/`);
}

/**
 * Builds a progress URL for a given course, then passes it to a callback.
 *
 * @param {string} courseID - The course identifier.
 * @param {(url: string) => void} callback - Function that receives the constructed URL.
 *
 * @example
 * getLmsCourseProgressUrl("course-v1:Demo+T101+2025", (url) => {
 *   console.log(url);
 *   // "https://example.com/learning/course/course-v1:Demo+T101+2025/progress"
 * });
 */
export function getLmsCourseProgressUrl(courseID: string, callback: (url: string) => void): void {
  callback(`${getLmsLearningUrl()}/course/${courseID}/progress`);
}
